import React, { useState } from 'react';
import api from '../services/api';

interface Props {
  onClose: () => void;
  accentColor?: 'purple' | 'emerald' | 'blue';
}

function getStrength(password: string): { score: number; label: string; color: string; barColor: string } {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  // Normalize to 0-4
  const norm = Math.min(4, Math.floor((score / 6) * 4 + (score > 0 ? 1 : 0)));

  if (score === 0) return { score: 0, label: '', color: 'text-gray-400', barColor: 'bg-gray-600' };
  if (score <= 2) return { score: 1, label: 'Weak', color: 'text-red-400', barColor: 'bg-red-500' };
  if (score <= 3) return { score: 2, label: 'Fair', color: 'text-orange-400', barColor: 'bg-orange-400' };
  if (score <= 4) return { score: 3, label: 'Good', color: 'text-yellow-400', barColor: 'bg-yellow-400' };
  return { score: 4, label: 'Strong', color: 'text-green-400', barColor: 'bg-green-500' };
}

const ACCENT = {
  purple: { ring: 'focus:border-purple-400', btn: 'bg-purple-600 hover:bg-purple-700' },
  emerald: { ring: 'focus:border-emerald-400', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  blue: { ring: 'focus:border-blue-400', btn: 'bg-blue-600 hover:bg-blue-700' },
};

const ChangePasswordModal: React.FC<Props> = ({ onClose, accentColor = 'purple' }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const strength = getStrength(newPassword);
  const accent = ACCENT[accentColor];

  const criteria = [
    { label: 'At least 8 characters', met: newPassword.length >= 8 },
    { label: 'Uppercase letter (A–Z)', met: /[A-Z]/.test(newPassword) },
    { label: 'Lowercase letter (a–z)', met: /[a-z]/.test(newPassword) },
    { label: 'Number (0–9)', met: /[0-9]/.test(newPassword) },
    { label: 'Special character (!@#$…)', met: /[^A-Za-z0-9]/.test(newPassword) },
  ];

  const handleSave = async () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await api.put('/auth/change-password', { newPassword });
      setSuccess(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-md w-full p-8"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}>

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white">Update Your Password</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-xl leading-none">✕</button>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">✅</div>
            <p className="text-white font-semibold text-lg mb-2">Password Updated!</p>
            <p className="text-white/60 text-sm mb-6">Your password has been changed successfully.</p>
            <button
              onClick={onClose}
              className={`${accent.btn} text-white px-8 py-2.5 rounded-lg font-semibold transition`}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* New Password */}
            <div className="mb-4">
              <label className="block text-white/70 text-xs font-semibold mb-1 uppercase tracking-wide">New Password</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className={`w-full px-4 py-2.5 pr-12 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none ${accent.ring} text-sm`}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showNew ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>

            {/* Strength Bar */}
            {newPassword.length > 0 && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-white/50">Password strength</span>
                  <span className={`text-xs font-semibold ${strength.color}`}>{strength.label}</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden flex gap-0.5">
                  {[1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className={`flex-1 rounded-full transition-all duration-300 ${i <= strength.score ? strength.barColor : 'bg-white/10'}`}
                    />
                  ))}
                </div>

                {/* Criteria checklist */}
                <ul className="mt-3 space-y-1">
                  {criteria.map(c => (
                    <li key={c.label} className="flex items-center gap-2 text-xs">
                      <span className={c.met ? 'text-green-400' : 'text-white/30'}>
                        {c.met ? '✓' : '○'}
                      </span>
                      <span className={c.met ? 'text-white/70' : 'text-white/30'}>{c.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Confirm Password */}
            <div className="mb-5">
              <label className="block text-white/70 text-xs font-semibold mb-1 uppercase tracking-wide">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  className={`w-full px-4 py-2.5 pr-12 bg-white/10 border rounded-lg text-white placeholder-white/30 focus:outline-none text-sm transition ${
                    confirmPassword.length > 0
                      ? confirmPassword === newPassword
                        ? 'border-green-500/60'
                        : 'border-red-500/60'
                      : 'border-white/20'
                  } ${accent.ring}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showConfirm ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
              {confirmPassword.length > 0 && confirmPassword !== newPassword && (
                <p className="text-red-400 text-xs mt-1">Passwords do not match.</p>
              )}
            </div>

            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white py-2.5 rounded-lg font-semibold transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className={`flex-1 ${accent.btn} disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold transition text-sm`}
              >
                {loading ? 'Saving...' : 'Save Password'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
